// Canonical domain -> brand derivation, shared by the browser bundle (src/lib/brand.ts),
// the self-hosted server (server.mjs) and the Vercel HTML function (api/og-index.js).
// One build serves many mirror domains: the brand shown in the UI, <title> and Open Graph
// tags is always derived from the current hostname, never hardcoded.
export const FALLBACK_BRAND = "Monvravex";

const IGNORED_SUBDOMAIN_PREFIXES = new Set(["www", "back", "app", "api", "m"]);
const COUNTRY_SECOND_LEVELS = new Set(["ac", "co", "com", "edu", "gov", "net", "org"]);
const BRAND_ASSET_EXTENSIONS = { favicon: ["svg", "png"], logo: ["svg", "png"], badge: ["svg", "png"] };

export function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function hostnameParts(domain) {
  const raw = String(domain ?? "").trim().toLowerCase();
  if (!raw) return [];
  let hostname = raw;
  try {
    hostname = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname;
  } catch {
    hostname = raw.split("/")[0]?.split(":")[0] ?? "";
  }
  return hostname.replace(/^\.+|\.+$/g, "").split(".").filter(Boolean);
}

/** Registrable label + tld for a domain, e.g. "www.domain.com" -> { name: "Domain", tld: ".com" }. */
export function brandPartsFromDomain(domain) {
  const parts = hostnameParts(domain);
  if (parts.length === 0) return { name: "", tld: "" };
  if (parts.length === 1 || parts.every((part) => /^\d+$/.test(part))) {
    const single = parts[0] ?? "";
    return { name: /^\d+$/.test(single) ? "Localhost" : capitalize(single), tld: "" };
  }

  while (parts.length > 2 && IGNORED_SUBDOMAIN_PREFIXES.has(parts[0] ?? "")) parts.shift();
  const last = parts[parts.length - 1] ?? "";
  const beforeLast = parts[parts.length - 2] ?? "";
  const hasCountrySecondLevel = parts.length >= 3 && last.length === 2 && COUNTRY_SECOND_LEVELS.has(beforeLast);
  const nameIndex = hasCountrySecondLevel ? parts.length - 3 : parts.length - 2;
  return {
    name: capitalize(parts[Math.max(0, nameIndex)] ?? ""),
    tld: `.${parts.slice(nameIndex + 1).join(".")}`
  };
}

/** Domain-derived brand, with a default for local development. */
export function resolveBrand(hostname) {
  const host = String(hostname ?? "").split(":")[0].toLowerCase();
  if (host === "" || host === "localhost" || host === "127.0.0.1") return { name: FALLBACK_BRAND, tld: "" };
  const derived = brandPartsFromDomain(hostname);
  return derived.name ? derived : { name: FALLBACK_BRAND, tld: "" };
}

export function getBackendOrigin(hostname) {
  const { name, tld } = brandPartsFromDomain(hostname);
  return name ? `https://back.${name.toLowerCase()}${tld}` : "";
}

/** Asset-path-safe slug for a brand name (e.g. "Monvravex" -> "monvravex"), used to look up per-domain favicon/logo/badge overrides. */
export function getBrandSlug(name) {
  return String(name ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Candidate public paths for a per-domain brand asset, most preferred first. Empty when the slug is empty. */
export function brandAssetCandidates(kind, slug) {
  if (!slug) return [];
  const extensions = BRAND_ASSET_EXTENSIONS[kind] ?? [];
  return extensions.map((extension) => `/brand/${slug}/${kind}.${extension}`);
}
